import React, { ReactElement } from 'react';
import { useDispatch } from 'react-redux';
import { openModal, hideModal } from 'cards/actions/modalActions';
import { getCardSettings } from 'cards/util/cardSettingsUtils';
import { CARD_TYPES } from 'cards/constants/CardTypes';
import type { WCardSuiteSummaryView } from '@zola/svc-web-api-ts-client';
import {
  useMostPopularSamplesFromFamily,
  useSingleSampleLimitExhausted,
  useModalCloseCallback,
} from '../util/hooks';
import { CardSuiteInfoSummary } from '../util/types';

import styles from './SingleSampleAccessPoint.module.less';

interface SingleSampleAccessPointProps {
  familyName?: string;
  displayComponent: ReactElement;
  sampleCardType?: string;
  cardSuiteInfoSummary?: CardSuiteInfoSummary;
  cardSuiteFilterFn?: (suites: WCardSuiteSummaryView[]) => WCardSuiteSummaryView;
}
const SingleSampleAccessPoint: React.FC<SingleSampleAccessPointProps> = ({
  familyName,
  displayComponent,
  sampleCardType = CARD_TYPES.invitation,
  cardSuiteInfoSummary,
  cardSuiteFilterFn,
}) => {
  const cardSuiteInfo =
    cardSuiteInfoSummary ||
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useMostPopularSamplesFromFamily(familyName, sampleCardType, cardSuiteFilterFn);
  const [sampleLimitExhausted, fetchSampleLimitExhausted] = useSingleSampleLimitExhausted(
    sampleCardType
  );
  const closeCallback = useModalCloseCallback();
  const dispatch = useDispatch();

  if (!cardSuiteInfo) return null;

  const {
    uuid,
    cardType,
    isMagnet,
    isLetterpress,
    singleSampleAvailable,
    singleSampleSku,
    sampleCard,
  } = cardSuiteInfo;

  // TODO: Confirm response returns only suites that are available
  if (!singleSampleAvailable) {
    return null;
  }

  function handleOpenModal(overrideProps = {}): void {
    const originalModalOptions = {
      warnOnClose: false,
      preventClose: true,
      closeCallback,
    };
    const modalProps = {
      entryPoint: 'WEBSITE',
      card: sampleCard?.lead_card,
      cardSuiteUUID: uuid,
      isMagnet,
      isLetterpress,
      sampleLimitExhausted,
      fetchSampleLimitExhausted,
      handleOpenModal,
      initialCloseCallback: closeCallback,
      closeModal: (): { type: string } => dispatch(hideModal()),
      originalModalOptions,
      nonEditableSingleSampleSku: singleSampleSku,
      ...overrideProps,
    };
    const modalOptions = {
      bottomModalOnMobile: true,
      className: styles.modal,
      ...originalModalOptions,
    };

    dispatch(openModal('SINGLE_SAMPLE_MODAL', modalProps, modalOptions));
  }

  return (
    <>
      {React.cloneElement(displayComponent, {
        handleOpenModal,
        cardTypeLabel: getCardSettings(cardType).label,
        singleSampleAvailable,
        cardSuiteInfo,
      })}
    </>
  );
};

export default SingleSampleAccessPoint;
